import React, { useState } from "react";

const CompleteBridgeAdventure = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  const completeStep = (stepIndex) => {
    if (!completedSteps.includes(stepIndex)) {
      setCompletedSteps([...completedSteps, stepIndex]);
    }
    if (stepIndex === currentStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex <= completedSteps.length) {
      setCurrentStep(stepIndex);
    }
  };

  const resetAdventure = () => {
    setCurrentStep(0);
    setCompletedSteps([]);
  };

  const steps = [
    "神庙法阵",
    "石门钥匙",
    "村庄古井",
    "平衡投票",
    "桥上跳跃",
    "魔王对话",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900">
      {/* 导航栏 */}
      <div className="bg-slate-800 border-b-2 border-slate-600 p-2 sm:p-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-lg sm:text-2xl font-bold text-white text-center mb-2 sm:mb-4">
            启发式探索通关挑战-神秘拱桥的秘密
          </h1>
          <div className="flex justify-center gap-1 sm:gap-2 flex-wrap">
            {steps.map((step, index) => (
              <button
                key={index}
                onClick={() => goToStep(index)}
                disabled={index > completedSteps.length}
                className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-bold transition-all ${
                  index === currentStep
                    ? "bg-blue-600 text-white"
                    : completedSteps.includes(index)
                    ? "bg-green-600 text-white hover:bg-green-500"
                    : index <= completedSteps.length
                    ? "bg-gray-600 text-white hover:bg-gray-500"
                    : "bg-gray-400 text-gray-600 cursor-not-allowed"
                }`}
              >
                <span className="hidden sm:inline">{index + 1}. {step}</span>
                <span className="sm:hidden">{index + 1}</span>
                {completedSteps.includes(index) && " ✓"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="p-2 sm:p-8">
        {currentStep === 0 && (
          <Step1_Temple onComplete={() => completeStep(0)} />
        )}
        {currentStep === 1 && (
          <Step2_StoneDoor onComplete={() => completeStep(1)} />
        )}
        {currentStep === 2 && (
          <Step3_VillageWell onComplete={() => completeStep(2)} />
        )}
        {currentStep === 3 && (
          <Step4_BalancedVoting onComplete={() => completeStep(3)} />
        )}
        {currentStep === 4 && (
          <Step5_BridgeJumping onComplete={() => completeStep(4)} />
        )}
        {currentStep === 5 && (
          <Step6_FinalStory onComplete={() => completeStep(5)} />
        )}
      </div>

      {/* 重置按钮 */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={resetAdventure}
          className="bg-red-600 hover:bg-red-500 text-white px-2 sm:px-4 py-2 rounded-lg font-bold text-sm sm:text-base"
        >
          重新开始
        </button>
      </div>
    </div>
  );
};

// 第一步：神庙法阵 - 修复漂移问题
const Step1_Temple = ({ onComplete }) => {
  const [values, setValues] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");

  const adjacentSums = [7, 8, 9, 6, 9, 8, 9];
  const greenIndex = 4;
  const correctAnswer = [6, 1, 7, 2, 4, 5, 3];

  const handleCircleClick = (index) => {
    if (isSuccess) return;
    const currentValue = values[index];
    const nextValue = currentValue >= 7 ? 1 : currentValue + 1;

    const newValues = [...values];
    newValues[index] = nextValue;
    setValues(newValues);
    setMessage("");
  };

  const checkAnswer = () => {
    const greenValue = values[greenIndex];
    const correctGreenValue = correctAnswer[greenIndex];

    if (greenValue === 0) {
      setMessage("请先填入绿光圆圈的数字！");
      return;
    }

    if (greenValue === correctGreenValue) {
      setIsSuccess(true);
      setMessage("结界破解成功！神庙大门开启...");
      setTimeout(() => onComplete(), 2000);
    } else {
      setMessage("绿光未启，请重新思考...");
    }
  };

  const getCirclePosition = (index, total = 7) => {
    const angle = (index * 2 * Math.PI) / total - Math.PI / 2;
    // 响应式半径
    const radius = window.innerWidth < 640 ? 80 : 120;
    const centerX = window.innerWidth < 640 ? 150 : 200;
    const centerY = window.innerWidth < 640 ? 150 : 200;

    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  };

  const getSumPosition = (index, total = 7) => {
    const angle = ((index + 0.5) * 2 * Math.PI) / total - Math.PI / 2;
    // 响应式半径
    const radius = window.innerWidth < 640 ? 55 : 80;
    const centerX = window.innerWidth < 640 ? 150 : 200;
    const centerY = window.innerWidth < 640 ? 150 : 200;

    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  };

  // 响应式SVG尺寸
  const svgSize = window.innerWidth < 640 ? 300 : 400;
  const circleRadius = window.innerWidth < 640 ? 20 : 25;

  return (
    <div className="flex flex-col items-center justify-center bg-gradient-to-b from-amber-900 to-amber-700 p-2 sm:p-8 rounded-lg">
      <div className="bg-amber-800 rounded-lg p-4 sm:p-8 shadow-2xl border-4 border-amber-600 max-w-4xl w-full">
        <h2 className="text-xl sm:text-2xl font-bold text-amber-100 text-center mb-4 sm:mb-6">
          古老神庙的法阵
        </h2>

        <div className="text-center mb-4 sm:mb-6">
          <p className="text-amber-200 text-sm sm:text-lg mb-2 sm:mb-4">
            你被时空漩涡吸入后陷入昏迷。再次醒来时，发现自己躺在一个古老的神庙中。
          </p>
          <p className="text-amber-200 text-sm sm:text-lg mb-2 sm:mb-4">
            你想走出去，但出不去。脚下的圆形法阵发出微光，上面刻着：
          </p>
          <p className="text-amber-200 text-sm sm:text-lg">
            七数围坐、从一至七，其和已现、绿光方启
          </p>
        </div>

        <div className="relative flex justify-center">
          <svg width={svgSize} height={svgSize} className="mx-auto">
            <circle
              cx={svgSize/2}
              cy={svgSize/2}
              r={window.innerWidth < 640 ? 80 : 120}
              fill="none"
              stroke="rgba(245, 158, 11, 0.3)"
              strokeWidth="2"
              strokeDasharray="5,5"
            />

            {values.map((value, index) => {
              const pos = getCirclePosition(index);
              const isGreen = index === greenIndex;

              return (
                <g key={index}>
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={circleRadius}
                    fill={isGreen ? "#22c55e" : "#f59e0b"}
                    stroke={isGreen ? "#16a34a" : "#d97706"}
                    strokeWidth="3"
                    className="cursor-pointer transition-all duration-300"
                    style={{
                      transformOrigin: `${pos.x}px ${pos.y}px`,
                      transform: 'scale(1)',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                    onClick={() => handleCircleClick(index)}
                  />

                  <text
                    x={pos.x}
                    y={pos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="text-white font-bold pointer-events-none select-none"
                    fontSize={window.innerWidth < 640 ? "14" : "18"}
                    fill="white"
                  >
                    {value === 0 ? "?" : value}
                  </text>
                </g>
              );
            })}

            {adjacentSums.map((sum, index) => {
              const pos = getSumPosition(index);
              return (
                <text
                  key={`sum-${index}`}
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-amber-200 font-bold"
                  fontSize={window.innerWidth < 640 ? "14" : "18"}
                  fill="#fbbf24"
                >
                  {sum}
                </text>
              );
            })}

            <circle
              cx={svgSize/2}
              cy={svgSize/2}
              r={window.innerWidth < 640 ? 30 : 40}
              fill="none"
              stroke="#fbbf24"
              strokeWidth="2"
              opacity="0.6"
            />
            <text
              x={svgSize/2}
              y={svgSize/2}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-amber-200 font-bold"
              fontSize={window.innerWidth < 640 ? "10" : "14"}
              fill="#fbbf24"
            >
              法阵
            </text>
          </svg>
        </div>

        <div className="text-center mt-4 sm:mt-6">
          <button
            onClick={checkAnswer}
            disabled={isSuccess}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-bold text-sm sm:text-lg transition-all duration-300 ${
              isSuccess
                ? "bg-green-600 text-white cursor-not-allowed"
                : "bg-amber-600 hover:bg-amber-500 text-amber-100 hover:scale-105"
            }`}
          >
            {isSuccess ? "✓ 结界已破" : "破解结界"}
          </button>
        </div>

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-center font-bold text-sm sm:text-base ${
              isSuccess
                ? "bg-green-700 text-green-100"
                : "bg-red-700 text-red-100"
            }`}
          >
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

// 第二步：石门钥匙
const Step2_StoneDoor = ({ onComplete }) => {
  const [selectedKey, setSelectedKey] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");

  const correctAnswer = 0;

  const handleKeyClick = (keyNumber) => {
    if (isSuccess) return;
    setSelectedKey(keyNumber);
    setMessage("");
  };

  const tryKey = () => {
    if (selectedKey === null) {
      setMessage("请先选择一把钥匙！");
      return;
    }

    if (selectedKey === correctAnswer) {
      setIsSuccess(true);
      setMessage("石门轰然开启！古老的机关缓缓运转...");
      setTimeout(() => onComplete(), 2000);
    } else {
      setMessage("钥匙无法转动，请重新选择...");
      setSelectedKey(null);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center bg-gradient-to-b from-stone-900 to-stone-700 p-2 sm:p-8 rounded-lg">
      <div className="bg-stone-800 rounded-lg p-4 sm:p-8 shadow-2xl border-4 border-stone-600 max-w-4xl w-full">
        <h2 className="text-xl sm:text-3xl font-bold text-stone-100 text-center mb-4 sm:mb-6">
          神庙石门
        </h2>

        <div className="text-center mb-4 sm:mb-6">
          <p className="text-stone-200 mb-2 sm:mb-4 text-sm sm:text-base">
            离开法阵后，你发现神庙的出口被一扇巨大的石门封住。
          </p>
          <p className="text-stone-200 mb-2 sm:mb-4 text-sm sm:text-base">
            石门上刻着一串数字："123456789101112131415..."一直延伸到墙角。
          </p>
          <p className="text-stone-200 text-sm sm:text-base">
            石门下方刻着古老的咒文："二千之位，取一为匙"
          </p>
        </div>

        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="w-full max-w-sm sm:w-96 h-60 sm:h-80 bg-stone-700 border-4 border-stone-600 rounded-lg relative">
            <div className="absolute top-4 left-4 right-4">
              <div className="text-stone-300 text-xs sm:text-sm font-mono leading-tight break-all">
                123456789101112131415...
              </div>
              <div className="text-stone-400 text-xs mt-1 text-center">
                (数字串继续延伸...)
              </div>
            </div>

            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="text-amber-300 text-sm sm:text-xl font-bold text-center mb-4">
                二千之位，取一为匙
              </div>

              <div className="mx-auto w-12 sm:w-16 h-6 sm:h-8 bg-stone-900 rounded-full border-2 border-stone-500 relative">
                <div className="absolute inset-2 bg-stone-800 rounded-full">
                  {selectedKey !== null && (
                    <div
                      className={`w-full h-full rounded-full flex items-center justify-center text-white font-bold transition-all duration-300 text-xs sm:text-base ${
                        isSuccess
                          ? "bg-amber-500 animate-pulse"
                          : "bg-amber-600"
                      }`}
                    >
                      {selectedKey}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <h3 className="text-stone-200 text-sm sm:text-lg font-bold text-center mb-4">
            散落的古铜钥匙
          </h3>
          <div className="grid grid-cols-5 sm:flex sm:flex-wrap sm:justify-center gap-2 sm:gap-3">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((keyNumber) => (
              <button
                key={keyNumber}
                onClick={() => handleKeyClick(keyNumber)}
                disabled={isSuccess}
                className={`w-10 h-12 sm:w-12 sm:h-16 rounded-lg border-2 font-bold text-sm sm:text-lg transition-all duration-300 ${
                  selectedKey === keyNumber
                    ? "bg-amber-500 border-amber-400 text-white scale-110 shadow-lg"
                    : isSuccess
                    ? "bg-stone-600 border-stone-500 text-stone-400 cursor-not-allowed"
                    : "bg-amber-600 border-amber-500 text-amber-100 hover:bg-amber-500 hover:scale-105 cursor-pointer"
                }`}
              >
                {keyNumber}
              </button>
            ))}
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={tryKey}
            disabled={isSuccess || selectedKey === null}
            className={`px-4 sm:px-8 py-2 sm:py-3 rounded-lg font-bold text-sm sm:text-lg transition-all duration-300 ${
              isSuccess
                ? "bg-green-600 text-white cursor-not-allowed"
                : selectedKey === null
                ? "bg-stone-600 text-stone-400 cursor-not-allowed"
                : "bg-amber-600 hover:bg-amber-500 text-amber-100 hover:scale-105"
            }`}
          >
            {isSuccess ? "✓ 石门已开" : "使用钥匙"}
          </button>
        </div>

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-center font-bold text-sm sm:text-base ${
              isSuccess
                ? "bg-green-700 text-green-100"
                : "bg-red-700 text-red-100"
            }`}
          >
            {message}
          </div>
        )}

        <div className="mt-4 text-stone-300 text-xs sm:text-sm text-center">
          <p>数字串从1开始：123456789101112131415...</p>
          <p>找到第2000个数字，选择对应的钥匙开启石门</p>
        </div>
      </div>
    </div>
  );
};

// 第三步：村庄古井 - 添加触摸支持和响应式布局
const Step3_VillageWell = ({ onComplete }) => {
  const [coins, setCoins] = useState([
    { id: 1, x: 200, y: 100, isDragging: false },
    { id: 2, x: 250, y: 150, isDragging: false },
    { id: 3, x: 300, y: 200, isDragging: false },
    { id: 4, x: 150, y: 250, isDragging: false },
    { id: 5, x: 200, y: 300, isDragging: false },
    { id: 6, x: 250, y: 350, isDragging: false },
    { id: 7, x: 100, y: 400, isDragging: false },
    { id: 8, x: 350, y: 450, isDragging: false },
  ]);

  const [draggedCoin, setDraggedCoin] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [showWater, setShowWater] = useState(false);

  // 响应式容器尺寸
  const containerSize = window.innerWidth < 640 ? 350 : 500;

  const checkSolution = () => {
    const tolerance = 30;

    const horizontalLines = [];
    coins.forEach((coin) => {
      let found = false;
      for (let line of horizontalLines) {
        if (Math.abs(coin.y - line.y) < tolerance) {
          line.coins.push(coin);
          found = true;
          break;
        }
      }
      if (!found) {
        horizontalLines.push({ y: coin.y, coins: [coin] });
      }
    });

    const verticalLines = [];
    coins.forEach((coin) => {
      let found = false;
      for (let line of verticalLines) {
        if (Math.abs(coin.x - line.x) < tolerance) {
          line.coins.push(coin);
          found = true;
          break;
        }
      }
      if (!found) {
        verticalLines.push({ x: coin.x, coins: [coin] });
      }
    });

    const hasHorizontal5 = horizontalLines.some(
      (line) => line.coins.length >= 5
    );
    const hasVertical5 = verticalLines.some((line) => line.coins.length >= 5);

    return hasHorizontal5 && hasVertical5;
  };

  // 统一的拖拽处理函数
  const handleDragStart = (e, coin) => {
    if (isSuccess) return;

    let clientX, clientY;
    if (e.type === 'mousedown') {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if (e.type === 'touchstart') {
      e.preventDefault();
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }

    const rect = e.currentTarget.parentElement.getBoundingClientRect();

    setDraggedCoin(coin.id);
    setDragOffset({
      x: clientX - rect.left - coin.x,
      y: clientY - rect.top - coin.y,
    });

    setCoins(
      coins.map((c) => (c.id === coin.id ? { ...c, isDragging: true } : c))
    );
  };

  const handleDragMove = (e) => {
    if (!draggedCoin) return;

    let clientX, clientY;
    if (e.type === 'mousemove') {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if (e.type === 'touchmove') {
      e.preventDefault();
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const newX = clientX - rect.left - dragOffset.x;
    const newY = clientY - rect.top - dragOffset.y;

    setCoins(
      coins.map((coin) =>
        coin.id === draggedCoin
          ? {
              ...coin,
              x: Math.max(15, Math.min(containerSize - 15, newX)),
              y: Math.max(15, Math.min(containerSize - 15, newY)),
            }
          : coin
      )
    );
  };

  const handleDragEnd = () => {
    setDraggedCoin(null);
    setDragOffset({ x: 0, y: 0 });
    setCoins(coins.map((coin) => ({ ...coin, isDragging: false })));
  };

  const checkWell = () => {
    if (checkSolution()) {
      setIsSuccess(true);
      setShowWater(true);
      setMessage("古井重新涌出清泉！村民们欢呼起来...");
      setTimeout(() => onComplete(), 2000);
    } else {
      setMessage("硬币的排列还不对，请重新尝试...");
    }
  };

  const setHintPosition = () => {
    const scale = containerSize / 500; // 缩放比例
    const hintCoins = [
      { id: 1, x: 250 * scale, y: 150 * scale, isDragging: false },
      { id: 2, x: 150 * scale, y: 250 * scale, isDragging: false },
      { id: 3, x: 200 * scale, y: 250 * scale, isDragging: false },
      { id: 4, x: 250 * scale, y: 250 * scale, isDragging: false },
      { id: 5, x: 300 * scale, y: 250 * scale, isDragging: false },
      { id: 6, x: 350 * scale, y: 250 * scale, isDragging: false },
      { id: 7, x: 250 * scale, y: 350 * scale, isDragging: false },
      { id: 8, x: 250 * scale, y: 400 * scale, isDragging: false },
    ];
    setCoins(hintCoins);
  };

  return (
    <div className="flex flex-col items-center justify-center bg-gradient-to-b from-sky-200 to-green-300 p-2 sm:p-8 rounded-lg">
      <div className="bg-amber-50 rounded-lg p-4 sm:p-8 shadow-2xl border-4 border-amber-200 max-w-4xl w-full">
        <h2 className="text-xl sm:text-3xl font-bold text-amber-800 text-center mb-4 sm:mb-6">
          村庄古井
        </h2>

        <div className="text-center mb-4 sm:mb-6">
          <p className="text-amber-700 mb-2 sm:mb-4 text-sm sm:text-base">
            石门轰然打开后，你看到前方是一个安静的村庄。
          </p>
          <p className="text-amber-700 mb-2 sm:mb-4 text-sm sm:text-base">
            村民们都坐在自家门前，看起来很疲惫，嘴唇干裂，似乎很久没有喝过水了。
          </p>
          <p className="text-amber-700 text-sm sm:text-base">
            村庄中央有一口古井，但井里已经完全干涸了。井壁上刻着："横看成岭侧成峰，五五相望显神通。重排古币，井水涌现。"
          </p>
        </div>

        <div className="relative mx-auto mb-4 sm:mb-6 flex justify-center">
          <div
            className="bg-stone-200 border-4 border-stone-400 rounded-lg relative overflow-hidden touch-none"
            style={{ width: containerSize, height: containerSize }}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div 
              className="absolute rounded-full border-4 border-stone-500"
              style={{
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: containerSize * 0.16,
                height: containerSize * 0.16,
                backgroundColor: '#475569'
              }}
            >
              {showWater && (
                <div
                  className="w-full h-full rounded-full animate-pulse"
                  style={{
                    background: "radial-gradient(circle, #60a5fa, #3b82f6)",
                    animation: "pulse 2s infinite",
                  }}
                ></div>
              )}
            </div>

            <div className="absolute top-2 sm:top-4 left-2 sm:left-4 right-2 sm:right-4 text-center">
              <div className="text-amber-700 font-bold text-xs sm:text-sm">
                横看成岭侧成峰，五五相望显神通
              </div>
              <div className="text-amber-600 text-xs mt-1">
                重排古币，井水涌现
              </div>
            </div>

            {coins.map((coin) => (
              <div
                key={coin.id}
                className={`absolute rounded-full border-2 cursor-pointer transition-all duration-200 select-none ${
                  coin.isDragging
                    ? "bg-yellow-400 border-yellow-600 scale-110 shadow-lg z-10"
                    : "bg-yellow-500 border-yellow-700 hover:scale-105 hover:shadow-md"
                }`}
                style={{
                  left: coin.x - (containerSize * 0.032),
                  top: coin.y - (containerSize * 0.032),
                  width: containerSize * 0.064,
                  height: containerSize * 0.064,
                  background: coin.isDragging
                    ? "radial-gradient(circle, #fbbf24, #f59e0b)"
                    : "radial-gradient(circle, #f59e0b, #d97706)",
                }}
                onMouseDown={(e) => handleDragStart(e, coin)}
                onTouchStart={(e) => handleDragStart(e, coin)}
              >
                <div className="w-full h-full flex items-center justify-center">
                  <div 
                    className="bg-yellow-700 rounded-full"
                    style={{
                      width: containerSize * 0.024,
                      height: containerSize * 0.024,
                    }}
                  ></div>
                </div>
              </div>
            ))}

            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20">
              <defs>
                <pattern
                  id="grid"
                  width="50"
                  height="50"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 50 0 L 0 0 0 50"
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth="1"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
        </div>

        <div className="text-center mb-4">
          <button
            onClick={checkWell}
            disabled={isSuccess}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-bold text-sm sm:text-lg mr-2 sm:mr-4 transition-all duration-300 ${
              isSuccess
                ? "bg-green-600 text-white cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-500 text-white hover:scale-105"
            }`}
          >
            {isSuccess ? "✓ 井水涌现" : "检查排列"}
          </button>

          {!isSuccess && (
            <button
              onClick={setHintPosition}
              className="px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-bold text-sm sm:text-lg bg-amber-600 hover:bg-amber-500 text-white transition-all duration-300"
            >
              显示提示
            </button>
          )}
        </div>

        {message && (
          <div
            className={`mb-4 p-3 rounded-lg text-center font-bold text-sm sm:text-base ${
              isSuccess
                ? "bg-green-100 text-green-800 border border-green-300"
                : "bg-red-100 text-red-800 border border-red-300"
            }`}
          >
            {message}
          </div>
        )}

        <div className="text-amber-700 text-xs sm:text-sm text-center">
          <p>拖动古币到合适的位置，让横向和纵向都有5个硬币</p>
          <p className="text-xs text-amber-600 mt-1">
            提示：试试摆成十字形或其他能让横竖都有5个硬币的形状
          </p>
        </div>
      </div>
    </div>
  );
};

// 第四步：平衡投票
const Step4_BalancedVoting = ({ onComplete }) => {
  const [selectedButton, setSelectedButton] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [showBarrier, setShowBarrier] = useState(true);
  const [showCounting, setShowCounting] = useState(false);

  const gridPattern = [
    [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
  ];

  const countBlackAndWhite = () => {
    let blackCount = 0;
    let whiteCount = 0;

    gridPattern.forEach((row) => {
      row.forEach((cell) => {
        if (cell === 1) blackCount++;
        else whiteCount++;
      });
    });

    return { blackCount, whiteCount };
  };

  const { blackCount, whiteCount } = countBlackAndWhite();
  const correctAnswer = blackCount > whiteCount ? "white" : "black";

  const handleButtonClick = (buttonType) => {
    if (isSuccess) return;
    setSelectedButton(buttonType);
    setMessage("");
  };

  const castVote = () => {
    if (!selectedButton) {
      setMessage("请先选择要投票的按钮！");
      return;
    }

    if (selectedButton === correctAnswer) {
      setIsSuccess(true);
      setShowBarrier(false);
      setMessage("投票成功！桥梁屏障消失，道路畅通...");
      setTimeout(() => onComplete(), 2000);
    } else {
      setMessage("投票失败，屏障依然存在，请重新思考...");
      setSelectedButton(null);
    }
  };

  const toggleCounting = () => {
    setShowCounting(!showCounting);
  };

  return (
    <div className="flex flex-col items-center justify-center bg-gradient-to-b from-gray-600 to-gray-800 p-2 sm:p-8 rounded-lg">
      <div className="bg-stone-100 rounded-lg p-4 sm:p-8 shadow-2xl border-4 border-stone-300 max-w-5xl w-full">
        <h2 className="text-xl sm:text-3xl font-bold text-stone-800 text-center mb-4 sm:mb-6">
          平衡的投票
        </h2>

        <div className="text-center mb-4 sm:mb-6">
          <p className="text-stone-700 mb-2 sm:mb-4 text-sm sm:text-base">
            村民们喝到清甜的井水后，终于能够开口说话了。
          </p>
          <p className="text-stone-700 mb-2 sm:mb-4 text-sm sm:text-base">
            村长告诉你："还有很多村民被困在桥的另一端，只有通过那座桥才能救他们。"
          </p>
          <p className="text-stone-700 text-sm sm:text-base">
            你来到峡谷边，发现桥的入口被无形屏障挡住。桥头石碑上刻着：
          </p>
        </div>

        <div className="relative mb-4 sm:mb-6">
          <div className="w-full h-24 sm:h-32 bg-stone-300 border-2 border-stone-400 rounded-lg relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-stone-400 to-stone-500">
              <div
                className="absolute inset-0 opacity-50"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(90deg, transparent, transparent 20px, rgba(0,0,0,0.1) 20px, rgba(0,0,0,0.1) 22px)",
                }}
              ></div>
            </div>

            {showBarrier && (
              <div className="absolute inset-0 bg-blue-400 bg-opacity-30 animate-pulse">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-blue-800 font-bold text-sm sm:text-lg">
                    ⚡ 神秘屏障 ⚡
                  </div>
                </div>
              </div>
            )}

            {isSuccess && (
              <div className="absolute inset-0 bg-green-400 bg-opacity-20 animate-pulse">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-green-800 font-bold text-sm sm:text-lg">
                    ✨ 道路畅通 ✨
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-stone-200 border-2 border-stone-400 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="text-center">
            <div className="text-stone-700 font-bold text-base sm:text-lg mb-2">
              黑为反对，白为同意
            </div>
            <div className="text-stone-600 text-sm sm:text-base mb-2">
              历代投票，已成定局
            </div>
            <div className="text-stone-600 text-sm sm:text-base">还差一票，决定去留</div>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <div className="text-center mb-4">
            <h3 className="text-stone-700 font-bold text-base sm:text-lg">历代投票记录</h3>
            <button
              onClick={toggleCounting}
              className="mt-2 px-3 sm:px-4 py-1 sm:py-2 bg-stone-500 hover:bg-stone-400 text-white rounded-lg text-xs sm:text-sm"
            >
              {showCounting ? "隐藏计数" : "显示计数"}
            </button>
          </div>

          <div className="flex justify-center">
            <div className="inline-block border-2 border-stone-400 p-1 sm:p-2 bg-white rounded-lg">
              <div className="grid grid-cols-11 gap-0.5 sm:gap-1">
                {gridPattern.map((row, rowIndex) =>
                  row.map((cell, colIndex) => (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className={`w-4 h-4 sm:w-6 sm:h-6 border border-gray-300 relative ${
                        cell === 1 ? "bg-black" : "bg-white"
                      }`}
                    >
                      {showCounting && (
                        <div
                          className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${
                            cell === 1 ? "text-white" : "text-black"
                          }`}
                        >
                          {cell === 1 ? "●" : "○"}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {showCounting && (
            <div className="text-center mt-4 text-stone-600">
              <div className="text-sm sm:text-lg font-bold">
                黑色方块: {blackCount} 个 | 白色方块: {whiteCount} 个
              </div>
              <div className="text-xs sm:text-sm mt-2">
                {blackCount > whiteCount
                  ? "黑色更多，需要投白票来平衡"
                  : "白色更多，需要投黑票来平衡"}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-center gap-8 sm:gap-12 mb-4 sm:mb-6">
          <div className="text-center">
            <button
              onClick={() => handleButtonClick("black")}
              disabled={isSuccess}
              className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 transition-all duration-300 ${
                selectedButton === "black"
                  ? "bg-black border-gray-600 scale-110 shadow-lg"
                  : isSuccess
                  ? "bg-gray-400 border-gray-500 cursor-not-allowed"
                  : "bg-black border-gray-700 hover:scale-105 cursor-pointer"
              }`}
            >
              {selectedButton === "black" && (
                <div className="text-white font-bold text-sm sm:text-lg">✓</div>
              )}
            </button>
            <div className="text-stone-700 font-bold mt-2 text-sm sm:text-base">反对</div>
          </div>

          <div className="text-center">
            <button
              onClick={() => handleButtonClick("white")}
              disabled={isSuccess}
              className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 transition-all duration-300 ${
                selectedButton === "white"
                  ? "bg-white border-gray-400 scale-110 shadow-lg"
                  : isSuccess
                  ? "bg-gray-200 border-gray-300 cursor-not-allowed"
                  : "bg-white border-gray-500 hover:scale-105 cursor-pointer"
              }`}
            >
              {selectedButton === "white" && (
                <div className="text-black font-bold text-sm sm:text-lg">✓</div>
              )}
            </button>
            <div className="text-stone-700 font-bold mt-2 text-sm sm:text-base">同意</div>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={castVote}
            disabled={isSuccess || !selectedButton}
            className={`px-4 sm:px-8 py-2 sm:py-3 rounded-lg font-bold text-sm sm:text-lg transition-all duration-300 ${
              isSuccess
                ? "bg-green-600 text-white cursor-not-allowed"
                : !selectedButton
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-500 text-white hover:scale-105"
            }`}
          >
            {isSuccess ? "✓ 屏障消失" : "投票"}
          </button>
        </div>

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-center font-bold text-sm sm:text-base ${
              isSuccess
                ? "bg-green-100 text-green-800 border border-green-300"
                : "bg-red-100 text-red-800 border border-red-300"
            }`}
          >
            {message}
          </div>
        )}

        <div className="mt-4 text-stone-600 text-xs sm:text-sm text-center">
          <p>观察投票记录方阵，数出黑色和白色方块的数量</p>
          <p>投出关键一票，让投票结果达到平衡</p>
        </div>
      </div>
    </div>
  );
};

// 第五步：桥上跳跃
const Step5_BridgeJumping = ({ onComplete }) => {
  const [answer, setAnswer] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [showHint, setShowHint] = useState(false);

  const correctAnswer = 411;

  const handleAnswerSubmit = () => {
    const userAnswer = parseInt(answer);

    if (isNaN(userAnswer)) {
      setMessage("请输入一个有效的数字！");
      return;
    }

    if (userAnswer === correctAnswer) {
      setIsSuccess(true);
      setMessage("正确！你找到了最少的步数，成功通过了试炼！");
      setTimeout(() => onComplete(), 2000);
    } else {
      setMessage(`不对，请再想想...（提示：答案在400-420之间）`);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center bg-gradient-to-b from-purple-900 to-indigo-900 p-2 sm:p-8 rounded-lg">
      <div className="bg-slate-800 rounded-lg p-4 sm:p-8 shadow-2xl border-4 border-slate-600 max-w-5xl w-full">
        <h2 className="text-xl sm:text-3xl font-bold text-slate-100 text-center mb-4 sm:mb-6">
          桥上的跳跃试炼
        </h2>

        <div className="bg-slate-700 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-slate-600">
          <div className="text-slate-200 text-center text-sm sm:text-base">
            <p className="mb-2">当你踏上石桥的那一刻，桥面突然发生了变化...</p>
            <p className="mb-2">
              原本平坦的桥面变成了2023块发光的石板，一直延伸到远方。
            </p>
            <p className="text-amber-300 font-bold">
              一个低沉的声音响起："勇士，你只能向右跳5格，或向左跳3格。用最少的步数到达第2023块石板！"
            </p>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <div className="bg-slate-600 rounded-lg p-3 sm:p-4 border-2 border-slate-500">
            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 mb-4">
              <div className="text-center">
                <div className="bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg font-bold text-sm sm:text-base">
                  向右跳 →
                </div>
                <div className="text-slate-300 text-xs sm:text-sm mt-1">前进5格</div>
              </div>
              <div className="text-center">
                <div className="bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg font-bold text-sm sm:text-base">
                  向左跳 ←
                </div>
                <div className="text-slate-300 text-xs sm:text-sm mt-1">后退3格</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <div className="text-center mb-4">
            <label className="text-slate-200 text-sm sm:text-lg font-bold block mb-2">
              请输入最少需要多少步才能到达第2023块石板：
            </label>
            <div className="flex items-center justify-center gap-2">
              <input
                type="number"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={isSuccess}
                className="w-20 sm:w-32 px-2 sm:px-4 py-2 text-lg sm:text-xl text-center border-2 border-slate-400 rounded-lg focus:border-blue-500 focus:outline-none"
                placeholder="???"
              />
              <span className="text-slate-300 text-sm sm:text-base">步</span>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnswerSubmit}
              disabled={isSuccess || !answer}
              className={`px-4 sm:px-8 py-2 sm:py-3 rounded-lg font-bold text-sm sm:text-lg transition-all duration-300 ${
                isSuccess
                  ? "bg-green-600 text-white cursor-not-allowed"
                  : !answer
                  ? "bg-slate-600 text-slate-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-500 text-white hover:scale-105"
              }`}
            >
              {isSuccess ? "✓ 试炼通过" : "确认答案"}
            </button>
          </div>
        </div>

        <div className="flex justify-center gap-4 mb-4 sm:mb-6">
          <button
            onClick={() => setShowHint(!showHint)}
            className="px-4 sm:px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg transition-all duration-300 text-sm sm:text-base"
          >
            {showHint ? "隐藏提示" : "显示提示"}
          </button>
        </div>

        {showHint && (
          <div className="bg-amber-800 border-2 border-amber-600 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="text-amber-200">
              <div className="font-bold mb-2 text-sm sm:text-base">解题提示：</div>
              <div className="text-xs sm:text-sm space-y-1">
                <p>• 设向右跳x次，向左跳y次</p>
                <p>• 列方程：5x - 3y = 2022（因为从第1块跳到第2023块）</p>
                <p>• 目标：最小化 x + y（总步数）</p>
                <p>• 使用数学方法求解不定方程</p>
                <p>• 答案在400-420之间</p>
              </div>
            </div>
          </div>
        )}

        {message && (
          <div
            className={`p-3 sm:p-4 rounded-lg text-center font-bold text-sm sm:text-base ${
              isSuccess
                ? "bg-green-700 text-green-100 border-2 border-green-500"
                : "bg-red-700 text-red-100 border-2 border-red-500"
            }`}
          >
            {message}
          </div>
        )}

        <div className="mt-4 sm:mt-6 text-slate-400 text-xs sm:text-sm text-center">
          <p>这是一个经典的不定方程问题：5x - 3y = 2022</p>
          <p>需要找到使 x + y 最小的正整数解</p>
        </div>
      </div>
    </div>
  );
};

// 第六步：最终故事
const Step6_FinalStory = ({ onComplete }) => {
  const [bridgeName, setBridgeName] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");

  const correctAnswer = "魔鬼桥";

  const handleSubmit = () => {
    if (bridgeName.trim() === correctAnswer) {
      setIsSuccess(true);
      setMessage(
        "太好了！诅咒解除，村民们获得了自由！桥梁现在成为了连接过去与未来的通道..."
      );
      setTimeout(() => onComplete(), 3000);
    } else {
      setMessage("不对，请再想想。回忆一下你一路走来收集到的线索...");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center bg-gradient-to-b from-purple-900 to-indigo-900 p-2 sm:p-8 rounded-lg">
      <div className="bg-slate-800 rounded-lg p-4 sm:p-8 shadow-2xl border-4 border-slate-600 max-w-5xl w-full">
        <h2 className="text-xl sm:text-3xl font-bold text-slate-100 text-center mb-4 sm:mb-6">
          魔王的倾诉
        </h2>

        <div className="bg-slate-700 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-slate-600">
          <div className="text-slate-200 space-y-2 sm:space-y-4 text-sm sm:text-base">
            <p>
              成功跳到桥的另一端后，一个高大的身影从迷雾中走出。这就是传说中的魔王，但他的眼中闪烁着复杂的光芒。
            </p>
            <p>
              "年轻人，你通过了所有考验，证明了你的智慧和勇气。现在我可以告诉你真相了。"
            </p>
            <p>
              "几百年前，村民们来找我，说他们需要一座桥来连接两岸，但工程太困难了。我答应帮助他们，条件是——第一个过桥的人，要将灵魂献给我。"
            </p>
            <p>
              "我信守承诺，一夜之间建成了这座桥。但村民们却用诡计欺骗了我，让一只羊先过桥，然后就再也不履行契约了。"
            </p>
            <p>
              "我愤怒地诅咒了这座桥和村庄，但经过这么多年...我也累了。现在，我想要一个公正的评判。"
            </p>
          </div>
        </div>

        <div className="bg-slate-700 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-slate-600">
          <div className="text-slate-200 space-y-2 sm:space-y-4 text-sm sm:text-base">
            <p>"我愿意释放所有村民，取消诅咒，但作为交换..."</p>
            <p>
              "我需要你告诉我，这座桥在传说中叫什么名字。如果你能说出来，就证明你理解了这个故事，我就心满意足了。"
            </p>
            <p className="text-amber-300 font-bold">
              "那座未来城市的居民，就是当年村民的后代。他们通过时空技术回到这里，想要修复祖先的过错。而你，应该是他们派来的使者吧？"
            </p>
            <p className="text-amber-300 font-bold">
              "去吧，告诉我这座桥的名字，完成你的使命。这座桥，将成为连接过去与未来的通道。"
            </p>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <div className="text-center mb-4">
            <label className="text-slate-200 text-sm sm:text-lg font-bold block mb-2">
              根据你收集到的所有线索，这座桥叫什么名字？
            </label>
            <input
              type="text"
              value={bridgeName}
              onChange={(e) => setBridgeName(e.target.value)}
              disabled={isSuccess}
              className="w-48 sm:w-64 px-3 sm:px-4 py-2 text-lg sm:text-xl text-center border-2 border-slate-400 rounded-lg focus:border-blue-500 focus:outline-none"
              placeholder="输入桥的名字..."
            />
          </div>

          <div className="text-center">
            <button
              onClick={handleSubmit}
              disabled={isSuccess || !bridgeName.trim()}
              className={`px-4 sm:px-8 py-2 sm:py-3 rounded-lg font-bold text-sm sm:text-lg transition-all duration-300 ${
                isSuccess
                  ? "bg-green-600 text-white cursor-not-allowed"
                  : !bridgeName.trim()
                  ? "bg-slate-600 text-slate-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-500 text-white hover:scale-105"
              }`}
            >
              {isSuccess ? "✓ 使命完成" : "说出答案"}
            </button>
          </div>
        </div>

        {message && (
          <div
            className={`p-3 sm:p-4 rounded-lg text-center font-bold text-sm sm:text-base ${
              isSuccess
                ? "bg-green-700 text-green-100 border-2 border-green-500"
                : "bg-red-700 text-red-100 border-2 border-red-500"
            }`}
          >
            {message}
          </div>
        )}

        <div className="mt-4 sm:mt-6 text-slate-400 text-xs sm:text-sm text-center">
          <p>回想一下你一路走来遇到的线索：</p>
          <p>恶灵封印、违背契约、大魔王的愤怒、午夜的救赎机会...</p>
        </div>
      </div>
    </div>
  );
};

export default CompleteBridgeAdventure;
